<template>
  <RouterLink
    :to="this.to"
    class="cursor-pointer hit"
    :class="{
      'hit-list': !compIsCard,
      portal: listingIs.inPortal,
      'for-sale': listingIs.forSale,
      bordered: !isHomepage,
      shadow,
      rounded: isHomepage
    }"
  >
    <!-- Card View -->
    <template v-if="compIsCard">
      <span class="rounded-t hit-media">
        <span class="flex hit-status">
          <UtilListingStatusTags :listing="listing" :showNew="listing.premium === 0" showVetCheck block />
        </span>

        <span v-if="showImage" class="rounded-t hit-img">
          <UtilImage :src="compMainImage" cover class="h-full">
            <template #loading>
              <UtilPlaceholder class="h-full" />
            </template>
          </UtilImage>
        </span>

        <span
          v-if="listingIs.inPortal && $store.getters['portals/getPortalFromSlug'](this.compPortalSlug)"
          class="hit-bar hit-portal-bar"
        >
          {{ $store.getters['portals/getPortalFromSlug'](this.compPortalSlug).name }}
        </span>

        <WidgetListingWatch
          v-if="!listingIs.owner && compCanWatch && listingIs.live"
          :listingCode="listing.listingCode"
          iconOnly
          data-tooltip="Add listing to your Watchlist"
          class="capitalize hit-watch tooltip"
          :class="{ 'hit-watch-force-visible': $breakpoint.smAndDown }"
        />
      </span>

      <span
        class="relative flex flex-col flex-grow pt-20"
        :class="{ 'px-2 pb-2': bordered, 'bg-background rounded': isHomepage }"
      >
        <span class="hit-uac">
          <div class="flex items-end w-full text-sm">
            <WidgetUac
              :user="listing.user && listing.user[0]"
              :company="listing.company && listing.company[0]"
              :showFlag="false"
              :bordered="false"
              :showUser="false"
              :showCompany="false"
              :locked="
                !ownerAlwaysVisible &&
                !listingIs.owner &&
                !isPostListing &&
                (listingIs.timeRestricted || !listingIs.detailsVisible)
              "
              compact
              :showMembershipStatus="showMembershipStatus"
              locationOrigin="listings search"
              class="flex-shrink-0"
              :userAlwaysUnlocked="ownerAlwaysVisible"
            />

            <span class="truncate hit-uac-name">
              <WidgetUac
                :user="listing.user && listing.user[0]"
                :company="listing.company && listing.company[0]"
                :showFlag="false"
                :bordered="false"
                :showAvatar="false"
                :locked="
                  !ownerAlwaysVisible &&
                  !listingIs.owner &&
                  !isPostListing &&
                  (listingIs.timeRestricted || !listingIs.detailsVisible)
                "
                :showMembershipStatus="showMembershipStatus"
                compact
                locationOrigin="listings search"
                :userAlwaysUnlocked="ownerAlwaysVisible"
              />
            </span>
          </div>
        </span>

        <h4
          v-if="listing.title || listing.fullTitle"
          class="flex-grow w-full mb-1 text-lg font-medium leading-tight clamp-2"
        >
          {{ $listingTitle(listing) }}
        </h4>

        <span class="flex flex-wrap w-full mb-1 text-sm text-gray-darker">
          <span v-if="!listingIs.timeRestricted" class="flex-shrink-0 mr-4 font-semibold text-primary">
            <UtilTextLink
              v-if="!appIs.auth && !ownerAlwaysVisible"
              @click.native.stop.prevent="
                $store.dispatch('modal/showModal', {
                  modalName: 'ModalAuthLogin',
                  modalData: { redirectToDashboard: false }
                })
              "
              class="font-medium underline cursor-pointer text-brand"
            >
              {{ $t('listing.loginToViewPricing') }}
            </UtilTextLink>

            <UtilPrice
              v-else-if="listing.price"
              :unitClass="isHomepage ? 'font-light' : 'font-normal'"
              :class="isHomepage ? 'font-light' : ''"
              :price="listing.price"
              shortUnit
            />
          </span>

          <span v-if="compAge" class="hit-age">
            <FontAwesomeIcon :icon="faCalendarAlt" class="mr-1" />
            {{ compAge }}
          </span>
        </span>

        <div v-if="!isHomepage || ownerAlwaysVisible" class="flex flex-wrap items-center justify-between">
          <UtilLocationSummary
            v-if="listing.location && listing.location.length"
            :location="listing.location[0]"
            :showLocality="(appIs.auth && listingIs.detailsVisible) || listingIs.owner || ownerAlwaysVisible"
            class="flex-shrink-0"
          />

          <div
            v-if="get(listing, 'location[0].milesAway') && listing.location[0].milesAway < 1000"
            class="text-sm text-gray"
          >
            <span v-if="Number(listing.location[0].milesAway) === 0">&lt; 1 mile</span>
            <span v-else>{{ Number(listing.location[0].milesAway).toLocaleString() }} miles</span>
          </div>
        </div>

        <UtilButton
          v-if="
            !isHomepage &&
            appIs.auth &&
            listingIs.owner &&
            listingIs.forSale &&
            !listingIs.auction &&
            compIsNotMatchMaker &&
            !listingIs.dead &&
            canMarkAsSold
          "
          color="secondary"
          class="mt-4"
          small
        >
          <FontAwesomeIcon :icon="faGavel" class="mr-1" fixed-width />
          <span>{{ $t('buttons.markAsSold') }}</span>
        </UtilButton>
      </span>
    </template>

    <!-- List view -->
    <template v-else>
      <UtilTileBase>
        <template v-if="showImage" #icon>
          <div class="hit-img-list">
            <UtilImage :src="compMainImage" style="height: 75px" cover rounded>
              <template #loading>
                <UtilPlaceholder class="w-full h-24 rounded" />
              </template>
            </UtilImage>
          </div>
        </template>

        <template #body>
          <div class="relative">
            <h4 class="flex items-center mb-1 text-base hit-title sm:pr-2" :class="compCanWatch ? 'pr-10' : ''">
              <span class="leading-snug clamp-2">
                <span class="font-medium">{{ $listingTitle(listing) }}</span>
              </span>
            </h4>

            <div class="flex flex-wrap items-center">
              <UtilListingStatusTags v-if="showStatusTags" :listing="listing" :showStatus="false" class="mr-2" />

              <div class="flex flex-wrap items-center justify-between">
                <UtilLocationSummary
                  v-if="listing.location && listing.location.length"
                  :location="listing.location[0]"
                  :showLocality="false"
                  class="mr-1"
                />

                <div v-if="get(listing, 'location[0].milesAway')" class="text-sm text-gray">
                  <span v-if="Number(listing.location[0].milesAway) === 0">&lt; 1 mile</span>
                  <span v-else>{{ Number(listing.location[0].milesAway).toLocaleString() }} miles</span>
                </div>
              </div>
            </div>

            <div class="flex w-full mt-1">
              <UtilTextLink
                v-if="!appIs.auth"
                @click.native.stop.prevent="
                  $store.dispatch('modal/showModal', {
                    modalName: 'ModalAuthLogin',
                    modalData: { redirectToDashboard: false }
                  })
                "
                class="text-sm font-semibold underline cursor-pointer text-brand"
              >
                {{ $t('listing.loginToViewPricing') }}
              </UtilTextLink>

              <UtilPrice
                v-else-if="listing.price"
                class="text-sm font-medium text-primary"
                unitClass="text-sm text-gray-darker font-normal"
                :price="listing.price"
              />

              <WidgetListingWatch
                v-if="!listingIs.owner && compCanWatch && listingIs.live && $breakpoint.smAndUp"
                :listingCode="listing.listingCode"
                class="py-0 text-sm text-left text-gray-darker sm:ml-4"
                :class="{ 'ml-2': compAge }"
                style="margin-top: -4px"
              />
            </div>

            <WidgetListingWatch
              v-if="!listingIs.owner && compCanWatch && listingIs.live && $breakpoint.xs"
              :listingCode="listing.listingCode"
              iconOnly
              class="hit-watch hit-watch-list hit-watch-force-visible"
            />
          </div>
        </template>
      </UtilTileBase>
    </template>
  </RouterLink>
</template>

<script>
import { faCalendarAlt, faEye, faGavel, faPencil, faStar } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import generateMapImageUrl from '@/lib/map-image'
import { listingType } from '@/lib/mapping'
import getLivestockAge from '@/lib/helpers/livestock-age'
import WidgetListingWatch from '@/components/widgets/WidgetListingWatch'
import WidgetUac from '@/components/widgets/WidgetUac'
import UtilPrice from '@/components/utils/UtilPrice'
import UtilTextLink from '@/components/utils/UtilTextLink'
import UtilTileBase from '@/components/utils/UtilTileBase'
import UtilImage from '@/components/utils/UtilImage'
import UtilPlaceholder from '@/components/utils/placeholders/UtilPlaceholder'
import UtilListingStatusTags from '@/components/utils/listing/UtilListingStatusTags'
import UtilLocationSummary from '@/components/utils/UtilLocationSummary'
import UtilButton from '@/components/utils/UtilButton'

export default {
  name: 'UtilHitCard',
  components: {
    WidgetListingWatch,
    WidgetUac,
    UtilTextLink,
    UtilPrice,
    UtilTileBase,
    UtilImage,
    UtilPlaceholder,
    UtilListingStatusTags,
    UtilLocationSummary,
    UtilButton
  },
  props: {
    type: String,
    layout: {
      type: String,
      default: 'card',
      validator: prop => ['card', 'list'].includes(prop)
    },
    listing: Object,
    to: {
      type: String,
      default: ''
    },
    canWatch: {
      type: Boolean,
      default: true
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    showImage: {
      type: Boolean,
      default: true
    },
    showStatusTags: {
      type: Boolean,
      default: true
    },
    showMembershipStatus: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: true
    },
    shadow: {
      type: Boolean,
      default: false
    },
    isHomepage: {
      type: Boolean,
      default: false,
      required: false
    },
    isPostListing: {
      type: Boolean,
      default: false
    },
    ownerAlwaysVisible: {
      type: Boolean,
      default: false
    },
    canMarkAsSold: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      listingType,
      get,
      faEye,
      faStar,
      faCalendarAlt,
      faPencil,
      faGavel
    }
  },
  computed: {
    listingIs() {
      return this.$store.getters['listing/listingIs'](this.listing, false, true)
    },

    compIsCard() {
      return this.layout === 'card'
    },

    compCanWatch() {
      return this.canWatch && this.listingIs.live
    },

    compTradeTag() {
      if (this.listing.type === listingType.CLASSIFIED) {
        return 'For Sale'
      } else if (this.listing.type === listingType.AUCTION) {
        return 'Auction'
      } else {
        return 'Wanted'
      }
    },

    compPortalSlug() {
      // Algolia and pete return it differently
      return typeof this.listing.portals[0] === 'string'
        ? this.listing.portals[0]
        : this.listing.portals[0].portalSlug
    },

    compMainImage() {
      let imageRef = ''

      // If we don't have an image show the map
      if (!this.listing.featureImageRef && !this.listing.imageFileRef) {
        // If the listing is part of a portal let's show their banner image
        if (this.listing.portals && this.listing.portals.length && this.compPortalSlug) {
          imageRef = this.$store.getters['portals/getPortalFromSlug'](this.compPortalSlug).bannerFileRef
        } else {
          if (this.listing.listingRef && this.listingIs.detailsVisible) {
            return generateMapImageUrl({
              listingRef: this.listing.listingRef,
              zoom: 9
            })
          } else {
            // If we don't have a listing location yet use the default one in their profile
            const country =
              this.listing.location && this.listing.location.length
                ? this.listing.location[0].country
                : this.app.locations[0].country

            let locality = null

            if (this.listingIs.detailsVisible) {
              locality =
                this.listing.location && this.listing.location.length
                  ? this.listing.location[0].locality
                  : this.app.locations[0].locality
            }

            const region =
              this.listing.location && this.listing.location.length
                ? this.listing.location[0].region
                : this.app.locations[0].region

            return generateMapImageUrl({
              country,
              locality: locality ? `${locality}, ${region}` : region,
              zoom: 9
            })
          }
        }
      } else {
        imageRef = `hc_${this.listing.featureImageRef || this.listing.imageFileRef}`
      }

      const imageBaseUrl = `${this.assetBaseUrl.imageTransform}${imageRef}?fit=crop`
      return this.layout === 'card' ? `${imageBaseUrl}&w=560&h=400` : `${imageBaseUrl}&w=200&h=75`
    },

    compAge() {
      if (!this.listingIs.livestock) {
        return ''
      }

      return getLivestockAge(this.listing)
    },

    compIsNotMatchMaker() {
      if (this.listing.matchmaker && this.listing.matchmaker.length === 0) {
        return true
      }

      if (this.listing.meta && !this.listing.meta.matchmaking) {
        return true
      }

      return false
    }
  },

  methods: {
    viewListing() {
      this.$router.push(this.to)
    }
  }
}
</script>

<style lang="scss" scoped>
$cardMediaSpacing: 8px;

.hit {
  @apply h-full flex flex-col;
}

.hit:not(.hit-list):hover {
  @apply shadow;
}

.hit.bordered {
  @apply bg-white rounded border border-transparent;
}

.hit.bordered .hit-list:hover {
  @apply shadow border border-gray;
}

.hit.bordered.gray {
  @apply bg-gray-400;
}

.hit-list:hover .hit-title {
  opacity: 0.8;
}

.hit-shadow {
  @apply shadow;

  &:hover {
    @apply shadow-xl;
  }
}

.hit-media {
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  @apply relative cursor-pointer;
}

.hit-img {
  @apply absolute top-0 left-0 h-full w-full overflow-hidden;
}

.hit-img-list {
  @apply rounded mr-2;
  width: 80px;

  img {
    @apply rounded;
  }
}

#app.mdAndUp .hit-img-list {
  width: 100px;
}

.hit-status {
  @apply flex items-center;
  position: absolute;
  top: $cardMediaSpacing;
  left: $cardMediaSpacing;
  z-index: 2;
}

.hit-watch {
  @apply text-primary bg-white shadow w-10 h-10 rounded-full border border-white;
  line-height: 0;
  position: absolute !important;
  top: $cardMediaSpacing;
  right: $cardMediaSpacing;
  opacity: 0;
  transition: opacity 0.5s;
  z-index: 2;

  &::v-deep svg {
    margin-right: 0;
  }

  &:after {
    line-height: 1;
  }
}

.hit-watch-force-visible {
  opacity: 1;
}

.hit:hover .hit-watch {
  opacity: 1;
}

.hit-watch-list {
  top: 0px;
  right: 0px;
}

.hit-uac {
  @apply absolute;
  top: -30px;
  left: $cardMediaSpacing;
  right: $cardMediaSpacing;
  z-index: 2;

  &::v-deep .avatar {
    width: 8rem;
    height: 8rem;
    border: 4px solid #fff;
  }

  &::v-deep .member-status {
    position: absolute;
    bottom: -4px;
    width: 100%;
  }
}

.hit-bar {
  @apply font-medium text-xs;
  height: 2.2rem;
  line-height: 2.2rem;
  padding-left: 9.8rem;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}

.hit-premuim-bar {
  @apply bg-blue-700 text-white bg-opacity-75 uppercase;
}

.hit-portal-bar {
  @apply bg-gray-darker text-white bg-opacity-75;
}

.hit-uac-name {
  @apply mb-1;

  &::v-deep .tile-content {
    padding: 0 !important;
  }

  &::v-deep .uac-body {
    margin-left: 0px;
  }
}

.hit-gradient {
  transition: opacity 0.25s ease-in-out;
}

.hit-user {
  @apply block;
}

.hit-business {
  @apply block;
}

.hit-action {
  @apply flex;
}
</style>
